import { Link } from 'react-router-dom';
import { useThemeClasses } from '../hooks/useTheme';
import { cn } from '../lib/utils';
import { Radio, ArrowLeft, Music, Mic, Monitor, ChevronRight } from 'lucide-react';

export default function StoopDAWHelpPage() {
  const { isDark, textClass, textMutedClass, textVeryMutedClass, borderClass } = useThemeClasses();

  const stepClass = cn(
    "flex gap-3 p-4 border rounded-sm mb-3",
    isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-gray-50"
  );

  const codeClass = cn(
    "inline px-1.5 py-0.5 rounded text-[11px] font-mono",
    isDark ? "bg-white/10 text-amber-400" : "bg-gray-100 text-amber-700"
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 mb-safe">
      {/* Back nav */}
      <Link
        to="/stoop"
        className={cn("inline-flex items-center gap-2 text-xs mb-6 hover:opacity-70 transition-opacity", textMutedClass)}
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to The Stoop
      </Link>

      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-sm bg-amber-500/20">
            <Radio className="h-5 w-5 text-amber-500" />
          </div>
          <h1 className={cn("font-display text-xl tracking-wider uppercase", textClass)}>
            DAW Routing Guide
          </h1>
        </div>
        <p className={cn("text-sm leading-relaxed", textMutedClass)}>
          How to route your DAW (GarageBand, Ableton, Logic Pro, FL Studio) into The Stoop so
          listeners hear your full mix — with music mode enabled.
        </p>
      </div>

      {/* What is Music Mode */}
      <section className="mb-8">
        <h2 className={cn("font-display text-sm tracking-widest uppercase mb-4", textVeryMutedClass)}>
          Voice Mode vs Music Mode
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className={cn("p-4 border rounded-sm", isDark ? "border-white/10" : "border-gray-200")}>
            <div className="flex items-center gap-2 mb-2">
              <Mic className="h-4 w-4 text-white" />
              <span className={cn("text-sm font-medium", textClass)}>Voice Mode (default)</span>
            </div>
            <ul className={cn("text-xs space-y-1", textMutedClass)}>
              <li>• Echo cancellation: ON</li>
              <li>• Noise suppression: ON</li>
              <li>• Auto gain control: ON</li>
              <li>Best for: conversations, talks, podcasting</li>
            </ul>
          </div>
          <div className={cn("p-4 border rounded-sm border-amber-500/30", isDark ? "bg-amber-500/5" : "bg-amber-50")}>
            <div className="flex items-center gap-2 mb-2">
              <Music className="h-4 w-4 text-amber-500" />
              <span className={cn("text-sm font-medium text-amber-500")}>Music Mode</span>
            </div>
            <ul className={cn("text-xs space-y-1", textMutedClass)}>
              <li>• Echo cancellation: OFF</li>
              <li>• Noise suppression: OFF</li>
              <li>• Auto gain control: OFF</li>
              <li>Best for: DAW routing, live sets, beats</li>
            </ul>
          </div>
        </div>
        <p className={cn("text-xs", textVeryMutedClass)}>
          Music mode sends your raw audio signal without browser processing. This prevents
          the browser from mangling your mix — essential when routing from a DAW or mixer.
        </p>
      </section>

      {/* Mac / Blackhole */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="h-4 w-4 text-amber-500" />
          <h2 className={cn("font-display text-sm tracking-widest uppercase", textClass)}>
            Mac — BlackHole Setup
          </h2>
        </div>

        <p className={cn("text-xs mb-4", textMutedClass)}>
          BlackHole is a free virtual audio driver for macOS that lets you route audio between apps.
        </p>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">01</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Install BlackHole</p>
            <p className={cn("text-xs", textMutedClass)}>
              Download from{' '}
              <span className={codeClass}>existential.audio/blackhole</span>
              {' '}and install the 2ch version (enough for stereo).
            </p>
          </div>
        </div>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">02</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Create a Multi-Output Device</p>
            <p className={cn("text-xs", textMutedClass)}>
              Open <span className={codeClass}>Audio MIDI Setup</span> (Spotlight → Audio MIDI Setup).
              Click <span className={codeClass}>+</span> → <span className={codeClass}>Create Multi-Output Device</span>.
              Check both <span className={codeClass}>BlackHole 2ch</span> and your speakers/headphones.
              Set it as your system output so you can monitor while routing.
            </p>
          </div>
        </div>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">03</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Set DAW output to BlackHole</p>
            <p className={cn("text-xs", textMutedClass)}>
              In your DAW's audio preferences, set the output device to{' '}
              <span className={codeClass}>BlackHole 2ch</span>.
              Your master bus now routes into BlackHole.
            </p>
          </div>
        </div>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">04</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Select BlackHole in The Stoop</p>
            <p className={cn("text-xs", textMutedClass)}>
              Join a Stoop as a speaker. In <strong>Audio Settings</strong>, open the Input Device
              dropdown and select <span className={codeClass}>BlackHole 2ch</span>.
              Switch to <span className={codeClass}>Music Mode</span> to disable echo cancellation.
            </p>
          </div>
        </div>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">05</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Turn on your mic</p>
            <p className={cn("text-xs", textMutedClass)}>
              Hit <strong>Turn On Mic</strong> in the controls. Your DAW output is now live.
              Play something — the room hears your mix in real time.
            </p>
          </div>
        </div>
      </section>

      {/* Windows / VB-Cable */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="h-4 w-4 text-amber-500" />
          <h2 className={cn("font-display text-sm tracking-widest uppercase", textClass)}>
            Windows — VB-Cable Setup
          </h2>
        </div>

        <p className={cn("text-xs mb-4", textMutedClass)}>
          VB-CABLE is the Windows equivalent of BlackHole — a virtual audio cable between apps.
        </p>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">01</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Install VB-CABLE</p>
            <p className={cn("text-xs", textMutedClass)}>
              Download from <span className={codeClass}>vb-audio.com/Cable</span> and run the installer
              as administrator. Restart if prompted.
            </p>
          </div>
        </div>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">02</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Route DAW output to CABLE Input</p>
            <p className={cn("text-xs", textMutedClass)}>
              In your DAW audio settings, set the output to <span className={codeClass}>CABLE Input (VB-Audio)</span>.
              To also hear your mix, use a monitoring bus or enable "listen to this device" in Windows Sound settings.
            </p>
          </div>
        </div>

        <div className={stepClass}>
          <span className="text-amber-500 font-display text-sm font-bold shrink-0">03</span>
          <div>
            <p className={cn("text-sm font-medium mb-1", textClass)}>Select CABLE Output in The Stoop</p>
            <p className={cn("text-xs", textMutedClass)}>
              In <strong>Audio Settings</strong>, choose <span className={codeClass}>CABLE Output (VB-Audio)</span>
              {' '}as your input device. Enable Music Mode, then turn on your mic.
            </p>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="mb-8">
        <h2 className={cn("font-display text-sm tracking-widest uppercase mb-4", textVeryMutedClass)}>
          Tips & Gotchas
        </h2>
        <ul className={cn("text-xs space-y-2.5", textMutedClass)}>
          <li className="flex gap-2">
            <ChevronRight className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              <strong className={textClass}>Volume:</strong> Keep your DAW master around −6 dB to avoid clipping in the stream.
            </span>
          </li>
          <li className="flex gap-2">
            <ChevronRight className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              <strong className={textClass}>Feedback:</strong> If you're also talking, add a separate mic track in your DAW routed to the virtual cable — don't use your physical mic directly in the browser at the same time.
            </span>
          </li>
          <li className="flex gap-2">
            <ChevronRight className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              <strong className={textClass}>Latency:</strong> Browser WebRTC adds ~50–200ms. This is fine for live performance but not for tracking (recording).
            </span>
          </li>
          <li className="flex gap-2">
            <ChevronRight className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              <strong className={textClass}>Permissions:</strong> On first use, your browser will ask for mic access. You must grant it, even though you're routing virtual audio — the browser sees it as a "microphone."
            </span>
          </li>
          <li className="flex gap-2">
            <ChevronRight className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              <strong className={textClass}>Device not showing?</strong> Open and close the Input Device dropdown after enabling your mic — the browser re-enumerates devices once permission is granted.
            </span>
          </li>
        </ul>
      </section>

      <div className={cn("border-t pt-6", borderClass)}>
        <p className={cn("text-xs", textVeryMutedClass)}>
          Questions? Drop it in{' '}
          <Link to="/gc" className="text-amber-500 underline">The GC</Link>.
        </p>
      </div>
    </div>
  );
}
